/**
 * Descriptor de Veículo. Todos os campos de `VeiculoOut`; a FIPE é derivada pelo
 * enriquecimento (read-only). Correlações: donos/condutores e documentos.
 */
import { Car } from 'lucide-react';
import { createElement } from 'react';
import { PainelConsultaVeicular } from '@/components/crud/paineis/PainelConsultaVeicular';
import { PainelDonosVeiculo } from '@/components/crud/paineis/PainelDonosVeiculo';
import { PainelFipe } from '@/components/crud/paineis/PainelFipe';
import { MarcaRastreado } from '@/components/crud/SinalRastreio';
import type { VeiculoCad } from '@/lib/api';
import { definirRecurso } from '@/lib/recursos/tipos';

const TIPO = [
  { valor: 'carro', rotulo: 'Carro' },
  { valor: 'moto', rotulo: 'Moto' },
  { valor: 'caminhao', rotulo: 'Caminhão' },
  { valor: 'onibus', rotulo: 'Ônibus' },
  { valor: 'outro', rotulo: 'Outro' },
];

export const veiculo = definirRecurso<VeiculoCad>({
  chave: 'veiculos',
  endpoint: '/cadastro/veiculos',
  ownerTipo: 'veiculo',
  titulo: 'Veículos',
  tituloSingular: 'veículo',
  descricao: 'Veículos com valor FIPE atualizado — donos e condutores.',
  icone: Car,
  capacidades: { podeEditar: true, podeExcluir: true },
  buscaPlaceholder: 'Buscar por placa, marca ou modelo',
  rotuloItem: (v) => v.placa || [v.marca, v.modelo].filter(Boolean).join(' ') || 'Veículo',

  colunas: [
    { chave: 'placa', rotulo: 'Placa', formato: 'placa', mono: true },
    { chave: 'marca', rotulo: 'Marca', ocultarEm: 'sm' },
    { chave: 'modelo', rotulo: 'Modelo' },
    // Saber que um carro está sendo seguido muda a leitura de tudo o mais que se vê na ficha —
    // é informação de lista, não de detalhe escondido.
    {
      chave: 'rastreado',
      rotulo: 'Rastreio',
      render: (v) => createElement(MarcaRastreado, { rastreado: v.rastreado }),
    },
  ],

  // `rastreado` não é campo do formulário (ninguém digita "este carro é rastreado"): é derivado da
  // instalação. Entra aqui para poder ser FILTRADO, que é o que `camposFiltro` existe para cobrir.
  camposFiltro: [
    {
      chave: 'rastreado',
      rotulo: 'Rastreado',
      tipo: 'booleano',
      ajuda: 'Veículos com rastreador instalado e rastreamento ligado.',
    },
  ],

  camposCriar: [
    { chave: 'placa', rotulo: 'Placa', tipo: 'placa' },
    { chave: 'modelo', rotulo: 'Modelo', tipo: 'texto' },
  ],

  // Cadastro por CRLV: importa o documento, a IA lê os dados e correlaciona o proprietário —
  // ou o operador insere à mão. Ver PainelDocumentos/ModalRevisaoExtracao.
  importar: {
    rotulo: 'Importar CRLV',
    tipoDocumento: 'crlv',
    descricao: 'Anexe o CRLV: a IA lê marca/modelo/placa/RENAVAM e correlaciona o proprietário. Você revisa antes de gravar.',
  },

  gruposEdicao: [
    {
      id: 'identificacao',
      titulo: 'Identificação',
      campos: [
        { chave: 'placa', rotulo: 'Placa', tipo: 'placa' },
        { chave: 'tipo', rotulo: 'Tipo', tipo: 'select', opcoes: TIPO },
        { chave: 'renavam', rotulo: 'RENAVAM', tipo: 'texto' },
        { chave: 'chassi', rotulo: 'Chassi', tipo: 'texto' },
        { chave: 'marca', rotulo: 'Marca', tipo: 'texto' },
        { chave: 'modelo', rotulo: 'Modelo', tipo: 'texto' },
        { chave: 'ano_fabricacao', rotulo: 'Ano de fabricação', tipo: 'numero' },
        { chave: 'ano_modelo', rotulo: 'Ano do modelo', tipo: 'numero' },
        { chave: 'cor', rotulo: 'Cor', tipo: 'texto' },
        { chave: 'combustivel', rotulo: 'Combustível', tipo: 'texto' },
        // Município/UF de emplacamento: a UF define o DETRAN da consulta de multas/restrições.
        // Preenchida pelo CRLV; editável aqui para veículos cadastrados à mão (de qualquer estado).
        { chave: 'municipio', rotulo: 'Município (emplacamento)', tipo: 'texto' },
        { chave: 'uf_placa', rotulo: 'UF da placa', tipo: 'texto' },
      ],
    },
    {
      id: 'fipe',
      titulo: 'FIPE (atualizada pelo enriquecimento)',
      campos: [
        { chave: 'valor_fipe', rotulo: 'Valor FIPE (R$)', tipo: 'leitura' },
        { chave: 'fipe_codigo', rotulo: 'Código FIPE', tipo: 'leitura' },
        { chave: 'fipe_mes_referencia', rotulo: 'Mês de referência', tipo: 'leitura' },
      ],
    },
    {
      id: 'sistema',
      titulo: 'Situação',
      campos: [
        { chave: 'ativo', rotulo: 'Ativo', tipo: 'booleano' },
        { chave: 'created_at', rotulo: 'Criado em', tipo: 'leitura' },
        { chave: 'updated_at', rotulo: 'Atualizado em', tipo: 'leitura' },
      ],
    },
  ],

  paineis: [
    {
      tipo: 'vinculos',
      titulo: 'Donos e condutores',
      endpointGet: '/cadastro/veiculos/{id}/vinculos',
      endpointPost: '/cadastro/veiculos/vinculos',
    },
    { tipo: 'documentos' },
    { tipo: 'dossie' },
  ],

  // Veículo não tem contato próprio; as abas cobrem dados/FIPE, os vínculos com pessoas/empresas,
  // documentos e a correlação (dossiê + grafo).
  abas: [
    { id: 'dados', rotulo: 'Dados do Veículo', grupos: ['identificacao', 'sistema'] },
    {
      id: 'fipe',
      rotulo: 'FIPE',
      // Botão para consultar/atualizar a FIPE sob demanda (além do enriquecimento no CRLV).
      paineis: [
        {
          tipo: 'custom',
          id: 'fipe',
          titulo: 'FIPE',
          render: ({ item, recarregar }) => createElement(PainelFipe, { item, recarregar }),
        },
      ],
    },
    {
      id: 'restricoes',
      rotulo: 'Restrições e Multas',
      // Consulta veicular (DETRAN): status, multas, gravames, licenciamento, débitos.
      paineis: [
        {
          tipo: 'custom',
          id: 'consulta-veicular',
          titulo: 'Restrições e Multas',
          render: ({ item }) => createElement(PainelConsultaVeicular, { item }),
        },
      ],
    },
    {
      id: 'vinculos',
      rotulo: 'Donos e Condutores',
      // Registro de propriedade: histórico com datas de compra/venda, 1 proprietário atual, tipo e
      // datas editáveis. A troca de dono também chega pelo DUT/ATPV-e (aba Documentos).
      paineis: [
        {
          tipo: 'custom',
          id: 'donos-veiculo',
          titulo: 'Donos e condutores',
          render: ({ item }) => createElement(PainelDonosVeiculo, { veiculoId: item.id }),
        },
      ],
    },
    {
      id: 'documentos',
      rotulo: 'Documentos',
      // Ofertará CRLV e DUT/ATPV-e (transferência) — o seletor de tipo filtra pelo owner 'veiculo'.
      paineis: [{ tipo: 'documentos' }],
    },
    { id: 'dossie', rotulo: 'Dossiê', paineis: [{ tipo: 'dossie' }] },
    { id: 'grafo', rotulo: 'Grafo', paineis: [{ tipo: 'grafo' }] },
  ],
});
